import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { useViewedFactor, useViewedFactorType } from '@/contexts/FactorLensContext/hooks'
import { useIsAnalysisLoading } from '@/contexts/RADashboardDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { CardProps } from '@/interfaces/raDashboard.interface'
import { cardStyle, cardSubtitleStyle, cardTitleStyle } from '@/styles/reportDashboardStyles'
import { Box, CircularProgress } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React from 'react'
import FactorWaterfall from './FactorWaterfall'
import { useFactorRiskColors } from './factorRiskColors'

/**
 * Standalone Factor Waterfall card, split out of FactorLensRisk and wired to the
 * shared FactorLensContext (data type stays in sync with the other Factor Lens cards).
 */
const FactorWaterfallCard: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const factorRiskColors = useFactorRiskColors()

  const selectedDataType = useViewedFactorType()
  const factorData = useViewedFactor()

  const dataLoading = useIsAnalysisLoading()

  const cardLoading = () => (
    <div style={{ margin: 5 }}>
      <CircularProgress size={'3rem'} aria-label="Loading…" />
    </div>
  )

  const cardContent = () => (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Data type follows the Decomposition card via shared context — no selector here. */}
      {/* Waterfall — re-key on data type so the chart fully re-renders.
          Hide FactorWaterfall's internal collapse header (arrow + redundant label)
          since this is a dedicated card with its own title. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          '& > div > div:first-of-type': { display: 'none !important' },
        }}
      >
        <FactorWaterfall
          key={selectedDataType}
          shown
          toggleVisibility={() => {}}
          factorData={factorData}
          colors={factorRiskColors}
        />
      </Box>
    </div>
  )

  return (
    <div
      style={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack padding={4} width={'100%'} sx={{ boxSizing: 'border-box', height: '100%' }}>
        <Typography style={{ ...cardTitleStyle, color: theme.textPrimary }}>
          {`${CardTitleLookup[props.cardId]} — ${selectedDataType} Risk`}
        </Typography>
        <Typography style={{ ...cardSubtitleStyle, color: theme.textSecondary }}>
          Contribution of each factor group to total risk
        </Typography>

        {dataLoading ? cardLoading() : cardContent()}
      </Stack>
    </div>
  )
}

export default FactorWaterfallCard
